import { useState } from "react";
import { CircleSlash, Info, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OPS } from "@/constants/testIds";
import { WM_TIMEOUT_MS, wmMessage } from "@/lib/wmError";
import { fmtQty } from "@/lib/wmDigits";

const C = OPS.wm;

/**
 * «لا يوجد هدر اليوم» / «لا يوجد أكل عمال اليوم» — writes a FLAG, never a
 * zero-quantity record, so a blank day is distinguishable from a forgotten
 * one from day one of the pilot.
 *
 * A mis-press is undone here, not by an administrator: the undo is a SOFT
 * reverse on the server (row kept, marked `reversed`, audit entry written).
 */
export function NoActivityButton({ day, branchId, date, type, onSaved }) {
    const { t, lang } = useI18n();
    const [busy, setBusy] = useState(false);
    const [undoOpen, setUndoOpen] = useState(false);
    const [reason, setReason] = useState("");
    const flagged = !!day?.day_status;
    const recordsCount = day?.records_count || 0;

    const undo = async () => {
        setBusy(true);
        try {
            await api.post("/waste-meals/day-status/reverse",
                           { branch_id: branchId, date, type, reason },
                           { timeout: WM_TIMEOUT_MS });
            toast.success(t("wm_no_activity_undone"));
            setUndoOpen(false);
            setReason("");
            onSaved && (await onSaved());
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    if (flagged) {
        return (
            <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-3 py-2.5">
                <CircleSlash className="h-4 w-4 text-[color:var(--ink-muted)]" />
                <span className="text-xs font-semibold text-[color:var(--ink)]">{t("wm_no_activity_saved")}</span>
                <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                    {day.day_status.closed_by_name}
                </Badge>
                <AlertDialog open={undoOpen} onOpenChange={setUndoOpen}>
                    <Button data-testid={C.noActivityUndo} variant="ghost" size="sm"
                            onClick={() => setUndoOpen(true)}
                            className="ms-auto h-7 gap-1.5 px-2 text-[11px] font-semibold">
                        <Undo2 className="h-3.5 w-3.5" />
                        {t("wm_no_activity_undo")}
                    </Button>
                    <AlertDialogContent data-testid={C.noActivityUndoDialog}>
                        <AlertDialogHeader>
                            <AlertDialogTitle>{t("wm_no_activity_undo_title")}</AlertDialogTitle>
                            <AlertDialogDescription>{t("wm_no_activity_undo_desc")}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <Input data-testid={C.noActivityUndoReason} value={reason}
                               onChange={(e) => setReason(e.target.value)}
                               placeholder={t("wm_no_activity_undo_reason")} className="h-9" />
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid={C.noActivityUndoCancel}>
                                {t("cancel")}
                            </AlertDialogCancel>
                            <AlertDialogAction data-testid={C.noActivityUndoConfirm} disabled={busy}
                                               onClick={(e) => { e.preventDefault(); undo(); }}>
                                {t("wm_no_activity_undo")}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        );
    }

    // The day already carries entries, so «لا يوجد» is a contradiction. The
    // server refuses it with WM_DAY_STATUS_CONFLICT; say WHY here and point at
    // the entries, because a greyed-out button with no reason reads as a bug
    // and a branch employee stops trusting the screen.
    if (recordsCount > 0) {
        return (
            <div data-testid={C.noActivityBlocked}
                 className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-[color:var(--warning-soft-fg)]/25 bg-[color:var(--warning-soft)] px-3 py-2.5">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
                <p className="text-xs font-semibold leading-relaxed text-[color:var(--warning-soft-fg)]">
                    {t("wm_no_activity_blocked").replace("{n}", fmtQty(recordsCount, lang))}
                </p>
            </div>
        );
    }

    const save = async () => {
        setBusy(true);
        try {
            await api.post("/waste-meals/day-status", { branch_id: branchId, date, type },
                           { timeout: WM_TIMEOUT_MS });
            toast.success(t("wm_no_activity_saved"));
            onSaved && (await onSaved());
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button data-testid={C.noActivity} variant="outline" onClick={save}
                disabled={busy} className="h-10 w-full gap-2 text-xs font-semibold">
            <CircleSlash className="h-4 w-4" />
            {type === "waste" ? t("wm_no_waste_today") : t("wm_no_meals_today")}
        </Button>
    );
}
